import React from 'react'
import { Box, Card, CardContent, Stack, Skeleton } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'

/**
 * Shimmer animation for skeleton loading
 */
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`

/**
 * Styled shimmer skeleton component
 */
const ShimmerSkeleton = styled(Skeleton)(({ theme }) => ({
  animation: `${shimmer} 2s infinite`,
  backgroundSize: '1000px 100%',
  backgroundImage: `linear-gradient(
    90deg,
    ${theme.palette.mode === 'dark' ? '#1a1a1a' : '#f0f0f0'} 25%,
    ${theme.palette.mode === 'dark' ? '#2a2a2a' : '#e0e0e0'} 50%,
    ${theme.palette.mode === 'dark' ? '#1a1a1a' : '#f0f0f0'} 75%
  )`
}))

/**
 * Skeleton for form fields
 */
export const FormFieldSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => (
  <Stack spacing={2}>
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index}>
        {/* Label skeleton */}
        <ShimmerSkeleton variant="text" width="30%" height={16} sx={{ mb: 1 }} />
        {/* Input skeleton */}
        <ShimmerSkeleton variant="rectangular" height={45} />
      </Box>
    ))}
  </Stack>
)

/**
 * Skeleton for card content
 */
export const CardSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <Card>
    <CardContent>
      <Stack spacing={2}>
        {/* Title skeleton */}
        <ShimmerSkeleton variant="text" width="40%" height={24} />
        {/* Content rows */}
        {Array.from({ length: rows }).map((_, index) => (
          <ShimmerSkeleton
            key={index}
            variant="text"
            width={index === rows - 1 ? '60%' : '100%'}
            height={16}
          />
        ))}
      </Stack>
    </CardContent>
  </Card>
)

/**
 * Skeleton for table rows
 */
export const TableRowSkeleton: React.FC<{ columns?: number; rows?: number }> = ({
  columns = 4,
  rows = 5
}) => (
  <Box>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box
        key={rowIndex}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 2,
          mb: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <ShimmerSkeleton key={colIndex} variant="text" height={20} />
        ))}
      </Box>
    ))}
  </Box>
)

/**
 * Skeleton for button
 */
export const ButtonSkeleton: React.FC<{ width?: string | number; height?: number }> = ({ 
  width = '260px', 
  height = 45 
}) => (
  <Box sx={{ m: 2.5 }}>
    <ShimmerSkeleton 
      variant="rectangular" 
      width={width}
      height={height}
      sx={{ borderRadius: '4px' }}
    />
  </Box>
)

/**
 * Skeleton for header with avatar and text
 */
export const HeaderSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
    {/* Avatar skeleton */}
    <ShimmerSkeleton variant="circular" width={48} height={48} />
    {/* Header text skeleton */}
    <Box sx={{ flex: 1 }}>
      <ShimmerSkeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
      <ShimmerSkeleton variant="text" width="60%" height={16} />
    </Box>
  </Box>
)

/**
 * Skeleton for list items
 */
export const ListItemSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <Box>
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        {/* Icon/Avatar skeleton */}
        <ShimmerSkeleton variant="circular" width={40} height={40} />
        {/* Text skeleton */}
        <Box sx={{ flex: 1 }}>
          <ShimmerSkeleton variant="text" width="80%" height={18} sx={{ mb: 1 }} />
          <ShimmerSkeleton variant="text" width="60%" height={14} />
        </Box>
      </Box>
    ))}
  </Box>
)

/**
 * Skeleton for guarantee details page
 */
export const GuaranteeDetailsSkeleton: React.FC = () => (
  <Box sx={{ p: 3 }}>
    {/* Header section */}
    <HeaderSkeleton />

    {/* Details cards */}
    <Stack spacing={3}>
      {Array.from({ length: 3 }).map((_, index) => (
        <CardSkeleton key={index} rows={4} />
      ))}
    </Stack>
  </Box>
)

/**
 * Skeleton for guarantee dashboard table
 */
export const GuaranteeDashboardTableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 10, 
  columns = 12 
}) => {
  const headerNames = ['Status', 'Type', 'IRN', 'Guarantee ID', 'Applicant', 'Applicant ID', 'Beneficiary', 'Beneficiary ID', 'Issuer', 'Issuer ID', 'Currency', 'Value']
  
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' }}>
        <thead>
          <tr style={{ backgroundColor: '#f5f6f8', borderBottom: '1px solid #dde1e4', borderRight: 'none', borderLeft: 'none' }}>
            {headerNames.slice(0, columns).map((header, index) => (
              <th 
                key={`header-${index}`} 
                style={{ 
                  padding: '12px 6px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'center',
                  width: `${100 / columns}%`,
                  fontWeight: 500,
                  fontSize: '12px',
                  color: '#333',
                  height: '44px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  {header}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr 
              key={`row-${rowIndex}`}
              style={{ 
                backgroundColor: rowIndex % 2 ? '#EAF8FF' : '#fff',
                borderBottom: '1px solid #dde1e4',
                height: '44px'
              }}
            >
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td 
                  key={`cell-${rowIndex}-${colIndex}`} 
                  style={{ 
                    padding: '12px 6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <ShimmerSkeleton 
                    variant="text" 
                    height={16}
                    width="75%"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#f5f6f8', borderTop: '1px solid #dde1e4' }}>
            <td colSpan={columns} style={{ padding: '12px 6px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ fontSize: '12px', color: '#666' }}>
                  <ShimmerSkeleton variant="text" width="200px" height={14} />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <ShimmerSkeleton 
                      key={`pagination-${index}`} 
                      variant="rectangular" 
                      width={32} 
                      height={28}
                      sx={{ borderRadius: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            </td>
          </tr>
        </tfoot>
      </table>
    </Box>
  )
}

/**
 * Skeleton for guarantee dashboard
 */
export const GuaranteeDashboardSkeleton: React.FC = () => (
  <Box sx={{ p: 2 }}>
    <GuaranteeDashboardTableSkeleton rows={10} columns={12} />
  </Box>
)

/**
 * Skeleton for contract history timeline
 */
export const ContractHistoryTimelineSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Box sx={{ width: 'calc(100% - 30px)', m: '0 15px', p: 2 }}>
    <Stack spacing={2}>
      {Array.from({ length: rows }).map((_, index) => (
        <Box
          key={`timeline-row-${index}`}
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f9f9f9',
            borderRadius: 1,
            borderLeft: '4px solid',
            borderColor: (theme) => theme.palette.mode === 'dark' ? '#444' : '#e0e0e0'
          }}
        >
          {/* Timeline icon circle */}
          <ShimmerSkeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ flexShrink: 0, mt: 1 }}
          />
          
          {/* Timeline content */}
          <Box sx={{ flex: 1 }}>
            <ShimmerSkeleton variant="text" height={16} width="60%" sx={{ mb: 1 }} />
            <ShimmerSkeleton variant="text" height={14} width="80%" sx={{ mb: 1 }} />
            <ShimmerSkeleton variant="text" height={14} width="40%" />
          </Box>

          {/* Timeline date */}
          <Box sx={{ flexShrink: 0 }}>
            <ShimmerSkeleton variant="text" height={14} width={80} />
          </Box>
        </Box>
      ))}
    </Stack>
  </Box>
)

/**
 * Skeleton for account section in sidebar
 */
export const AccountSkeleton: React.FC = () => (
  <Box
    sx={{
      m: 2.4,
      p: 2,
      backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f0f0',
      borderRadius: '4px'
    }}
  >
    <Stack spacing={1}>
      <ShimmerSkeleton variant="text" height={24} width="70%" />
      <ShimmerSkeleton variant="text" height={16} width="50%" />
    </Stack>
  </Box>
)

/**
 * Skeleton for navigation items in sidebar
 */
export const NavItemSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <Box sx={{ p: 2 }}>
    <Stack spacing={1.5}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={`nav-item-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Icon placeholder */}
          <ShimmerSkeleton
            variant="circular"
            width={24}
            height={24}
            sx={{ flexShrink: 0 }}
          />
          {/* Text placeholder */}
          <ShimmerSkeleton
            variant="text"
            height={16}
            width="60%"
            sx={{ flex: 1 }}
          />
        </Box>
      ))}
    </Stack>
  </Box>
)

export default {
  FormFieldSkeleton,
  CardSkeleton,
  TableRowSkeleton,
  HeaderSkeleton,
  ListItemSkeleton,
  GuaranteeDetailsSkeleton,
  GuaranteeDashboardTableSkeleton,
  GuaranteeDashboardSkeleton,
  ContractHistoryTimelineSkeleton,
  AccountSkeleton,
  NavItemSkeleton,
  ButtonSkeleton
}
